import { FileDown } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Badge, Button, Card, CardBody, CardHeader } from '../components/ui';
import { Donut, GroupedBars, HBar, Histogram, LineChart } from '../components/charts';
import { useStatsFull } from '../api/queries';
import { downloadFile } from '../api/client';

export function StatsScreen() {
  const { data, isLoading } = useStatsFull();
  if (isLoading || !data) {
    return (
      <div className="page-fade">
        <PageHeader title="Statistik" subtitle="Lade Daten…" />
        <div className="skeleton" style={{ height: 280, borderRadius: 12, marginBottom: 20 }} />
        <div className="stats-2col" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, marginBottom: 20 }}>
          <div className="skeleton" style={{ height: 220, borderRadius: 12 }} />
          <div className="skeleton" style={{ height: 220, borderRadius: 12 }} />
        </div>
        <div className="stats-2col" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20 }}>
          <div className="skeleton" style={{ height: 220, borderRadius: 12 }} />
          <div className="skeleton" style={{ height: 220, borderRadius: 12 }} />
        </div>
      </div>
    );
  }

  const sectionData = (data.sektionen ?? []).map((s) => ({ label: s.name, value: s.count, dot: s.color }));
  const genderData = [
    { color: '#007AFF', name: 'männlich', value: data.gender.m },
    { color: '#DB2777', name: 'weiblich', value: data.gender.w },
    { color: '#7C3AED', name: 'divers', value: data.gender.d },
  ].filter((d) => d.value > 0);
  const dateStr = new Date().toLocaleDateString('de-AT', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="page-fade">
      <PageHeader
        title="Statistik"
        subtitle={`Stand ${dateStr}`}
        actions={
          <Button
            variant="secondary"
            icon={<FileDown size={16} />}
            onClick={async () => { try { await downloadFile('/members/export.csv', 'mitglieder.csv'); } catch { /* */ } }}
          >
            Mitgliederliste exportieren
          </Button>
        }
      />

      <Card style={{ marginBottom: 20 }}>
        <CardHeader action={<Badge tone="success" dot>+ {data.growth.length > 1 ? (data.growth[data.growth.length - 1]!.count - data.growth[0]!.count) : 0} in 5 Jahren</Badge>}>
          Mitglieder-Wachstum
        </CardHeader>
        <CardBody><LineChart data={data.growth} height={220} /></CardBody>
      </Card>

      <div className="stats-2col" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, marginBottom: 20 }}>
        <Card>
          <CardHeader>Mitglieder pro Sektion</CardHeader>
          <CardBody><HBar data={sectionData} /></CardBody>
        </Card>
        <Card>
          <CardHeader>Geschlechterverteilung</CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', alignItems: 'center', gap: 20 }}>
              <Donut data={genderData} size={140} thickness={20} />
              <div className="donut-legend">
                {genderData.map((d) => (
                  <div className="lg-row" key={d.name}>
                    <span className="sw" style={{ background: d.color }} />
                    <span className="nm">{d.name}</span>
                    <span className="val t-num">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="stats-2col" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, marginBottom: 20 }}>
        <Card>
          <CardHeader>Altersverteilung</CardHeader>
          <CardBody><Histogram data={data.ageBuckets} color="#007AFF" /></CardBody>
        </Card>
        <Card>
          <CardHeader>Eintritte vs. Austritte</CardHeader>
          <CardBody><GroupedBars data={data.joinLeave} /></CardBody>
        </Card>
      </div>

    </div>
  );
}
